import { Injectable } from '@angular/core';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Country, CountryStateService, State } from '@vendasta/country-state';
import { TranslateService } from '@ngx-translate/core';
import { AddressService } from '@vendasta/businesses';
import { CountryConfiguration } from '@vendasta/address';

@Injectable({ providedIn: 'root' })
export class LocationService {
  private readonly countries$: Observable<Country[]>;
  private stateCache: Map<string, Observable<State[]>>;
  private countryCache: Map<string, Observable<CountryConfiguration>>;

  constructor(
    private readonly countryStateService: CountryStateService,
    private readonly addressService: AddressService,
    private readonly translate: TranslateService,
  ) {
    this.countries$ = this.countryStateService.getCountriesOptions().pipe(take(1), shareReplay(1));
    this.stateCache = new Map<string, Observable<State[]>>();
    this.countryCache = new Map<string, Observable<CountryConfiguration>>();
  }

  getCountries(): Observable<Country[]> {
    return this.countries$;
  }

  getCountryConfig(countryCode: string): Observable<CountryConfiguration> {
    if (this.countryCache.get(countryCode)) {
      return this.countryCache.get(countryCode);
    }

    this.countryCache.set(
      countryCode,
      this.addressService.getCountry(countryCode).pipe(
        catchError((err) => {
          console.error(err);
          return of(
            new CountryConfiguration({
              labels: {
                zone: this.translate.instant('AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.STATE'),
              },
            }),
          );
        }),
        take(1),
        shareReplay(1),
      ),
    );

    return this.countryCache.get(countryCode);
  }

  getStates(countryCode: string): Observable<State[]> {
    if (this.stateCache.get(countryCode)) {
      return this.stateCache.get(countryCode);
    }

    this.stateCache.set(
      countryCode,
      this.countryStateService.getStatesOptions(countryCode).pipe(
        map((states) => {
          states.unshift({
            // Keep label as just Any when using for listing to avoid inconsistent changes between labels when using with select input.
            name: this.translate.instant('AUTOMATIONS.EDITOR.FILTERS.ACCOUNT_GROUP_DATA.DATA_LABELS.ANY_STATE', {
              zone: '',
            }),
            code: '*',
          });
          return states;
        }),
        take(1),
        shareReplay(1),
      ),
    );

    return this.stateCache.get(countryCode);
  }

  getState(countryCode: string, stateCode: string): Observable<State> {
    return this.getStates(countryCode).pipe(map((states) => states.find((s) => s.code === stateCode)));
  }
}
